/**
 * LT-Estimate-UX-01 Section 8 dependency — Won / Lost outcome marker.
 *
 * A compact popover-based chip. Two flavours:
 *   • variant="chip"   → header-sized pill for TenderDetail.
 *   • variant="row"    → cell-sized pill for TendersList rows.
 *
 * Behaviour:
 *   • Un-marked (status ∉ {won, lost}): shows "Mark won / Mark lost".
 *   • Marked: shows the current outcome + the date it was recorded.
 *     Clicking opens a small popover with "Change to won", "Change to
 *     lost", "Clear".
 *   • Clearing sets the status back to "submitted" (single sensible
 *     pre-outcome state; the backend hard-codes this to avoid a
 *     three-way UI dialog for something that's a preview-only
 *     correction path).
 *
 * `onOutcomeChanged({ status, outcome_recorded_at, outcome_note })` is
 * fired after every successful mutation so callers can refresh their
 * own state (avoids a page reload).
 */
import React, { useState } from "react";
import { Check, ChevronDown, X, TrendingUp, TrendingDown } from "lucide-react";
import { toast } from "sonner";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "./ui/popover";
import { api } from "../lib/api";
import { ukDate } from "../lib/format";

const cls = (...xs) => xs.filter(Boolean).join(" ");

// Colours match StatusPill.jsx so the outcome chip reads as an
// enhanced status indicator, not a separate concept.
const STYLE = {
  unmarked: { bg: "#F5F1E9", fg: "#5A544A", border: "#E6DFCF",  hover: "#EDE6D3" },
  won:      { bg: "#D7E5DB", fg: "#1F3A2C", border: "#B9CFC0",  hover: "#C7DACD" },
  lost:     { bg: "#ECE9E1", fg: "#6B6B6B", border: "#D6D0C0",  hover: "#DAD3C1" },
};

function ActionRow({ icon: Icon, label, onClick, danger = false, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={cls(
        "w-full inline-flex items-center gap-2 px-3 py-2 rounded-md text-left",
        danger ? "text-note-red hover:bg-note-red/10" : "text-ink-2 hover:bg-surface-2 hover:text-ink"
      )}
      style={{ fontSize: 12.5 }}
    >
      <Icon size={13} className="flex-shrink-0" />
      {label}
    </button>
  );
}

export default function OutcomeChip({
  tender,
  onOutcomeChanged,
  variant = "chip",
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const status = (tender?.status || "").toLowerCase();
  const isMarked = status === "won" || status === "lost";
  const style = STYLE[isMarked ? status : "unmarked"];

  const send = async (outcome, note) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/tenders/${tender.id}/mark-outcome`, {
        outcome, note: note || null,
      });
      onOutcomeChanged?.(data);
      toast.success(
        outcome === "won" ? "Marked as won"
        : outcome === "lost" ? "Marked as lost"
        : "Outcome cleared"
      );
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update outcome");
    } finally {
      setBusy(false);
    }
  };

  const label = isMarked
    ? (status === "won" ? "Won" : "Lost")
    : (disabled ? "Not applicable" : "Mark won / lost");

  const dateSuffix = isMarked && tender.outcome_recorded_at
    ? ukDate(tender.outcome_recorded_at)
    : null;

  // -------------- Trigger ------------------------------------------------
  const isRow = variant === "row";
  const tooltip = disabled
    ? (tender?.deleted_at ? "Trashed — restore first"
       : tender?.status === "archived" ? "Archived — unarchive first"
       : tender?.status === "draft"    ? "Submit the tender before marking won or lost"
       : "Cannot mark outcome")
    : undefined;
  const trigger = (
    <button
      type="button"
      disabled={disabled || busy}
      title={tooltip}
      data-testid={`outcome-chip-${status || "unmarked"}${isRow ? "-row" : ""}`}
      className={cls(
        "inline-flex items-center gap-1.5 rounded-md transition-colors",
        isRow ? "" : "font-medium",
      )}
      style={{
        background: disabled ? "#F5F1E9" : style.bg,
        color: disabled ? "#9E9483" : style.fg,
        border: `1px ${disabled ? "dashed" : "solid"} ${disabled ? "#D8D0BE" : style.border}`,
        padding: isRow ? "1px 6px" : "3px 8px 3px 7px",
        fontSize: isRow ? 10 : 10.5,
        fontWeight: 600,
        letterSpacing: "0.05em",
        textTransform: "uppercase",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.55 : 1,
      }}
    >
      {isMarked && status === "won"  && <TrendingUp   size={isRow ? 9  : 11} />}
      {isMarked && status === "lost" && <TrendingDown size={isRow ? 9  : 11} />}
      <span>{label}</span>
      {dateSuffix && (
        <span
          className="font-mono tnum ml-1"
          style={{
            fontSize: isRow ? 9 : 9.5,
            fontWeight: 400,
            textTransform: "none",
            letterSpacing: 0,
            opacity: 0.75,
          }}
        >
          {dateSuffix}
        </span>
      )}
      <ChevronDown size={isRow ? 9 : 11} className={cls("opacity-70 ml-0.5", disabled && "hidden")} />
    </button>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={4}
        className="w-56 p-1.5"
        data-testid="outcome-chip-popover"
      >
        <div className="px-2 pt-1 pb-1.5 text-ink-3" style={{ fontSize: 10.5, letterSpacing: "0.06em", textTransform: "uppercase" }}>
          {isMarked ? `Currently ${status}` : "Record outcome"}
        </div>
        {(!isMarked || status !== "won") && (
          <ActionRow icon={TrendingUp} label="Mark as won"
            onClick={() => send("won")} testId="outcome-mark-won" />
        )}
        {(!isMarked || status !== "lost") && (
          <ActionRow icon={TrendingDown} label="Mark as lost"
            onClick={() => send("lost")} testId="outcome-mark-lost" />
        )}
        {isMarked && (
          <>
            <div className="h-px bg-line-soft my-1" />
            <ActionRow icon={X} label="Clear outcome" danger
              onClick={() => send("clear")} testId="outcome-clear" />
          </>
        )}
        {isMarked && tender.outcome_note && (
          <div className="px-2 pt-1.5 pb-1 text-ink-3 border-t border-line-soft mt-1" style={{ fontSize: 11, fontStyle: "italic" }}>
            “{tender.outcome_note}”
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
